import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  FaWallet,
  FaSync,
  FaSearch,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
} from "react-icons/fa";
import { API_URL } from "../../config/apiConfig";

const CorporateWallet = () => {
  const { user } = useSelector((state) => state.auth);

  const token = useMemo(() => {
    if (user?.token) return user.token;
    if (user?.user?.token) return user.user.token;
    if (user?.data?.token) return user.data.token;
    const storedAdminToken = localStorage.getItem("adminToken");
    if (storedAdminToken) return storedAdminToken;
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      return stored?.token || stored?.user?.token || stored?.data?.token || null;
    } catch (e) {
      return null;
    }
  }, [user]);

  const config = useMemo(() => {
    if (token) return { headers: { Authorization: `Bearer ${token}` } };
    return {};
  }, [token]);

  const [loading, setLoading] = useState(false);
  const [masterLoading, setMasterLoading] = useState(false);
  const [master, setMaster] = useState(null);
  const [transactions, setTransactions] = useState([]);

  const [topup, setTopup] = useState({ corporateAccountId: "", amount: "" });
  const [topupLoading, setTopupLoading] = useState(false);

  const [filters, setFilters] = useState({
    corporateAccountId: "",
    type: "all",
    status: "all",
    bookingId: "",
    from: "",
    to: "",
  });

  const fetchMaster = async () => {
    setMasterLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/corporate-wallet/master`, config);
      if (res.data && (res.data.success || res.data.status)) {
        setMaster(res.data.data || null);
      } else {
        setMaster(null);
      }
    } catch (e) {
      setMaster(null);
      toast.error(e?.response?.data?.message || e.message || "Failed to fetch master wallet");
    } finally {
      setMasterLoading(false);
    }
  };

  const submitTopup = async (e) => {
    e.preventDefault();
    const corporateAccountId = String(topup.corporateAccountId || "").trim();
    const amount = Number(topup.amount);

    if (!corporateAccountId) {
      toast.error("Corporate Account ID is required");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Amount must be a positive number");
      return;
    }

    setTopupLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/admin/corporate-wallet/topup`,
        { corporateAccountId, amount },
        config,
      );
      if (res.data && (res.data.success || res.data.status)) {
        toast.success("Topup successful");
        setTopup((p) => ({ ...p, amount: "" }));
        await Promise.all([fetchMaster(), fetchTransactions()]);
      } else {
        toast.error(res.data?.message || "Failed to topup");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to topup");
    } finally {
      setTopupLoading(false);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.corporateAccountId) params.set("corporateAccountId", filters.corporateAccountId);
      if (filters.type && filters.type !== "all") params.set("type", filters.type);
      if (filters.status && filters.status !== "all") params.set("status", filters.status);
      if (filters.bookingId) params.set("bookingId", filters.bookingId);
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);

      const res = await axios.get(`${API_URL}/admin/corporate-wallet/transactions?${params.toString()}`, config);
      if (res.data && (res.data.success || res.data.status)) {
        setTransactions(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setTransactions([]);
      }
    } catch (e) {
      setTransactions([]);
      toast.error(e?.response?.data?.message || e.message || "Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaster();
    fetchTransactions();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusBadge = (status) => {
    const s = String(status || "").toUpperCase();
    if (s === "SUCCESS") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          <FaCheckCircle className="mr-1" />
          SUCCESS
        </span>
      );
    }
    if (s === "FAILED") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
          <FaTimesCircle className="mr-1" />
          FAILED
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
        <FaClock className="mr-1" />
        {s || "PENDING"}
      </span>
    );
  };

  const onSearch = (e) => {
    e.preventDefault();
    fetchTransactions();
  };

  const setMasterStatus = async (nextStatus) => {
    try {
      const res = await axios.post(
        `${API_URL}/admin/corporate-wallet/master/status`,
        { status: nextStatus },
        config,
      );
      if (res.data && (res.data.success || res.data.status)) {
        setMaster(res.data.data || null);
        toast.success("Master wallet updated");
      } else {
        toast.error(res.data?.message || "Failed to update master wallet");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to update master wallet");
    }
  };

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div className="flex items-center mb-4 sm:mb-0">
          <FaWallet className="text-3xl text-blue-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Corporate Wallet</h1>
            <p className="text-gray-600 mt-1">Master wallet balance and corporate transaction ledger</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              fetchMaster();
              fetchTransactions();
            }}
            disabled={loading || masterLoading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <FaSync className={`mr-2 ${(loading || masterLoading) ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Topup Corporate Account</h2>
          <p className="text-sm text-gray-600 mt-1">Credits corporate wallet and master cash balance (prepaid model)</p>
        </div>
        <div className="p-6">
          <form onSubmit={submitTopup} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Corporate Account ID / Admin Email</label>
              <input
                value={topup.corporateAccountId}
                onChange={(e) => setTopup((p) => ({ ...p, corporateAccountId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Paste CorporateAccount _id or admin email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <input
                type="number"
                value={topup.amount}
                onChange={(e) => setTopup((p) => ({ ...p, amount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. 5000"
              />
            </div>

            <div className="md:col-span-3 flex justify-end">
              <button
                type="submit"
                disabled={topupLoading}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <FaWallet className="mr-2" />
                {topupLoading ? "Processing..." : "Topup"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <p className="text-sm font-medium text-gray-600 mb-1">Master Cash Balance</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(master?.cashBalance)}</p>
          <p className="text-xs text-gray-500 mt-1">Topups credited here (prepaid)</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <p className="text-sm font-medium text-gray-600 mb-1">Master Revenue Balance</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(master?.revenueBalance)}</p>
          <p className="text-xs text-gray-500 mt-1">Ride revenue recognition</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <p className="text-sm font-medium text-gray-600 mb-1">Master Wallet Status</p>
          <div className="flex items-center justify-between">
            <p className="text-lg font-semibold text-gray-900">{String(master?.status || "-").toUpperCase()}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMasterStatus("active")}
                className="px-3 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700"
              >
                Activate
              </button>
              <button
                onClick={() => setMasterStatus("suspended")}
                className="px-3 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700"
              >
                Suspend
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Corporate Transactions</h2>

          <form onSubmit={onSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Corporate Account ID</label>
              <input
                value={filters.corporateAccountId}
                onChange={(e) => setFilters((p) => ({ ...p, corporateAccountId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters((p) => ({ ...p, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All</option>
                <option value="WALLET_TOPUP">WALLET_TOPUP</option>
                <option value="RIDE_DEBIT">RIDE_DEBIT</option>
                <option value="RIDE_CREDIT">RIDE_CREDIT</option>
                <option value="REFUND">REFUND</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All</option>
                <option value="SUCCESS">SUCCESS</option>
                <option value="FAILED">FAILED</option>
                <option value="PENDING">PENDING</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Booking ID</label>
              <input
                value={filters.bookingId}
                onChange={(e) => setFilters((p) => ({ ...p, bookingId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={filters.from}
                  onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={filters.to}
                  onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="lg:col-span-6 flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <FaSearch className="mr-2" />
                Search
              </button>
            </div>
          </form>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No transactions
                  </td>
                </tr>
              ) : (
                transactions.map((t) => {
                  const company = t?.corporateAccountId?.companyName || "-";
                  const getBookingCode = (item) => {
                    if (typeof item.bookingId === 'object' && item.bookingId !== null && item.bookingId.bookingId) {
                      return item.bookingId.bookingId;
                    }
                    if (typeof item.bookingId === 'string' && item.bookingId.startsWith('CB')) {
                      return item.bookingId;
                    }
                    if (item.description) {
                      const match = item.description.match(/\b(CB[A-Z0-9]+)\b/i);
                      if (match) return match[1].toUpperCase();
                    }
                    return "-";
                  };
                  const booking = getBookingCode(t);
                  return (
                    <tr key={t._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(t.createdAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{company}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.type}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{formatCurrency(t.amount)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{statusBadge(t.status)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{booking}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CorporateWallet;
